import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Donation {
  id: string;
  created_at: string;
  display_name: string;
  message: string;
  amount: number;
  currency: string;
}

export const useDonations = () => {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [latestDonation, setLatestDonation] = useState<Donation | null>(null);

  const fetchDonations = async () => {
    const { data, error } = await supabase
      .from('donations')
      .select('*')
      .order('amount', { ascending: false });

    if (error) {
      console.error('Error fetching donations:', error);
      return;
    }

    setDonations(data || []);
    const total = (data || []).reduce((sum, d) => sum + Number(d.amount), 0);
    setTotalAmount(total);
    setLoading(false);
  };

  const addDonation = async (displayName: string, message: string, amount: number) => {
    const { data, error } = await supabase
      .from('donations')
      .insert({
        display_name: displayName.slice(0, 20),
        message: message.slice(0, 500),
        amount,
        currency: 'EUR'
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding donation:', error);
      throw error;
    }

    return data;
  };

  useEffect(() => {
    fetchDonations();

    // Subscribe to realtime updates with error handling
    const channel = supabase
      .channel('donations-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'donations'
        },
        (payload) => {
          const newDonation = payload.new as Donation;
          setLatestDonation(newDonation);
          setDonations((prev) => {
            const updated = [newDonation, ...prev];
            return updated.sort((a, b) => Number(b.amount) - Number(a.amount));
          });
          setTotalAmount((prev) => prev + Number(newDonation.amount));
        }
      )
      .subscribe((status, err) => {
        // Silently handle connection errors - realtime is optional enhancement
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Connection failed, but app still works with initial data fetch
          return;
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Get special badges
  const getHighestAllTime = () => donations[0] || null;
  
  const getMonthlyHighest = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthlyDonations = donations.filter(
      (d) => new Date(d.created_at) >= startOfMonth
    );
    return monthlyDonations.length > 0 ? monthlyDonations[0] : null;
  };

  const getNewestDonation = () => {
    if (donations.length === 0) return null;
    return [...donations].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  };

  return {
    donations,
    totalAmount,
    loading,
    latestDonation,
    addDonation,
    getHighestAllTime,
    getMonthlyHighest,
    getNewestDonation,
    refetch: fetchDonations
  };
};
